import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { ORDER_SEPARATOR } from '../sdk/react';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Row = { label: string; items: string[] };
const FILLERS = ['🪑 chair', '🔑 key', '📚 book', '⚽ ball', '🌳 tree', '👟 shoe', '🕰️ clock', '🎈 balloon', '🔨 hammer', '🧸 teddy', '☂️ umbrella', '🪥 toothbrush'];

export default function FindAll(props: GameProps) {
  const game = useGame(props);
  const rows = ((game.config?.sequences ?? []) as Row[]).filter((r) => r?.label && Array.isArray(r.items) && r.items.length > 0);
  const size = game.num('boardSize', 9);
  const [row, setRow] = useState<Row | null>(null);
  const [board, setBoard] = useState<string[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [trial, setTrial] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const busy = useRef(false);

  const start = useCallback(async () => {
    if (rows.length === 0) return;
    const next = pick(rows);
    const targets = shuffle(next.items).slice(0, Math.max(1, Math.min(next.items.length, size - 2)));
    const others = shuffle([
      ...rows.filter((r) => r !== next).flatMap((r) => r.items),
      ...FILLERS,
    ].filter((t) => !next.items.includes(t)));
    const tiles = shuffle([...targets, ...others.slice(0, size - targets.length)]);
    setDone(false);
    setPicked([]);
    const id = await game.register({ label: next.label, tiles }, { value: [...targets].sort().join(ORDER_SEPARATOR) });
    if (!id) return;
    setRow(next);
    setBoard(tiles);
    setTrial(id);
  }, [rows.map((r) => r.label + r.items.join()).join('|'), size]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!row || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(row.label), 300);
    return () => window.clearTimeout(t);
  }, [row, trial]);

  const targets = row ? board.filter((t) => row.items.includes(t)) : [];

  async function finish(sel: number[]) {
    if (!trial || !row || busy.current) return;
    busy.current = true;
    const response = sel.map((i) => board[i]).sort().join(ORDER_SEPARATOR);
    const v = await game.respond(trial, response);
    busy.current = false;
    if (v?.correct) { setDone(true); return; }
    game.say(row.label);
    const fresh = await game.register({ label: row.label, tiles: board, retry: true }, { value: [...targets].sort().join(ORDER_SEPARATOR) });
    if (fresh) { setTrial(fresh); setPicked([]); }
  }

  function tap(i: number) {
    if (!row || done) return;
    const isTarget = row.items.includes(board[i]);
    if (game.errorless && !isTarget) return;
    const next = picked.includes(i) ? picked.filter((p) => p !== i) : [...picked, i];
    setPicked(next);
    const found = next.filter((p) => row.items.includes(board[p])).length;
    if (found === targets.length && next.length === found) void finish(next);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });

  return (
    <GameFrame game={game} empty={rows.length === 0} emptyText={`${KINDS.findAll.describes} Edit it to add something to find.`} onResume={() => { void start(); }} breakTrial={trial}>
      {row && (
        <div className={`g-board${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={row.label} />
          <div className={`tile-grid ${board.length <= 4 ? 'cols-2' : board.length <= 9 ? 'cols-3' : 'cols-4'}`} role="group" aria-label="Pictures">
            {board.map((t, i) => {
              const f = splitFace(t);
              const on = picked.includes(i);
              return (
                <button
                  key={`${t}-${i}`}
                  className={`tile g-face${on ? ' is-picked' : ''}${done && on ? ' correct' : ''}`}
                  aria-pressed={on}
                  onClick={() => tap(i)}
                  disabled={done}
                  aria-label={f.word || t}
                >
                  {on && <span className="g-tick" aria-hidden="true">✓</span>}
                  <Pic text={t} fallback={f.picture ? <span className="g-tile-pic" aria-hidden="true">{f.picture}</span> : null} className="g-tile-img" />
                  {f.word && <span className="g-tile-word">{f.word}</span>}
                </button>
              );
            })}
          </div>
          {!game.errorless && !done && (
            <div className="toolbar g-done-bar">
              <button className="btn" disabled={picked.length === 0} onClick={() => void finish(picked)}>Done</button>
            </div>
          )}
        </div>
      )}
    </GameFrame>
  );
}
